import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { Alert, InteractionManager, Platform } from 'react-native';
import { Episode } from '@/types';
import { ContentService } from '@/services/contentService';
import { appStabilityGuard } from '@/utils/appStabilityGuard';
import { loginCoordinator } from '@/services/loginCoordinator';
import { campaignCache } from '@/services/campaignCacheService';
import { suppressPageViewsForPlayerLaunch, releasePageViewSuppression } from '@/utils/pageViewSuppression';

const DEBOUNCE_INTERVAL_MS = 300;

type PlayerLifecycleState = 'CLOSED' | 'OPENING' | 'OPEN' | 'CLOSING';

interface PlayerState {
  isVisible: boolean;
  episodes: Episode[] | undefined;
  seriesId: string | undefined;
  initialEpisodeId: string | undefined;
  startTimeSeconds: number | undefined;
  isLoading: boolean;
  isForYouMode: boolean;
  onCloseCallback: (() => void) | undefined;
}

interface PlayerContextType extends PlayerState {
  openPlayerWithSeries: (
    seriesId: string,
    campaignCountriesLanguagesId: string | null,
    smartuserId?: string,
    resumeEpisodeId?: string,
    startTimeSeconds?: number
  ) => Promise<void>;
  openPlayerWithEpisode: (
    episodeId: string,
    episodes: Episode[],
    startTimeSeconds?: number
  ) => void;
  openPlayerWithEpisodes: (
    episodes: Episode[],
    initialEpisodeId?: string,
    startTimeSeconds?: number,
    options?: { isForYouMode?: boolean; onCloseCallback?: () => void; seriesId?: string }
  ) => void;
  openPlayerForYou: (
    campaignCountriesLanguagesId: string | null,
    options?: { onCloseCallback?: () => void }
  ) => Promise<void>;
  closePlayer: () => void;
  setPlayerLoading: (loading: boolean) => void;
  wasClosedFromForYou: () => boolean;
  clearClosedFromForYouFlag: () => void;
  getLifecycleState: () => PlayerLifecycleState;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

const INITIAL_STATE: PlayerState = {
  isVisible: false,
  episodes: undefined,
  seriesId: undefined,
  initialEpisodeId: undefined,
  startTimeSeconds: undefined,
  isLoading: false,
  isForYouMode: false,
  onCloseCallback: undefined,
};

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [playerState, setPlayerState] = useState<PlayerState>(INITIAL_STATE);

  const lifecycleStateRef = useRef<PlayerLifecycleState>('CLOSED');
  const closedFromForYouRef = useRef(false);
  const pendingOpenRef = useRef<(() => void) | null>(null);
  const lastOpenTimeRef = useRef<number>(0);
  const openInProgressRef = useRef<boolean>(false);

  const getLifecycleState = useCallback(() => lifecycleStateRef.current, []);

  const setPlayerLoading = useCallback((loading: boolean) => {
    setPlayerState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const checkDebounceAndStability = useCallback(async (operationName: string): Promise<boolean> => {
    const now = Date.now();
    const timeSinceLastOpen = now - lastOpenTimeRef.current;

    if (timeSinceLastOpen < DEBOUNCE_INTERVAL_MS) {
      console.log(`🛑 [PlayerContext] ${operationName} debounced - only ${timeSinceLastOpen}ms since last open`);
      return false;
    }

    if (appStabilityGuard.isRecoveryInProgress()) {
      console.log(`⏳ [PlayerContext] ${operationName} - auth recovery in progress, waiting for completion...`);
      const isStable = await appStabilityGuard.waitForStability(3000);
      if (!isStable) {
        console.log(`⚠️ [PlayerContext] ${operationName} - recovery timeout, checking if still in progress`);
        if (appStabilityGuard.isRecoveryInProgress()) {
          console.log(`🛑 [PlayerContext] ${operationName} - still recovering, blocking operation`);
          return false;
        }
      }
      console.log(`✅ [PlayerContext] ${operationName} - recovery complete, proceeding`);
    }

    if (!appStabilityGuard.isStable()) {
      console.log(`⏳ [PlayerContext] ${operationName} - app not stable, waiting...`);
      const isStable = await appStabilityGuard.waitForStability(3000);
      if (!isStable) {
        console.log(`⚠️ [PlayerContext] ${operationName} - stability timeout, proceeding with caution`);
      }

      await new Promise<void>((resolve) => {
        InteractionManager.runAfterInteractions(() => {
          resolve();
        });
      });

      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });

      const extraDelay = Platform.OS === 'ios' ? 100 : 50;
      await new Promise((resolve) => setTimeout(resolve, extraDelay));
    }

    lastOpenTimeRef.current = now;
    return true;
  }, []);

  const closePlayerInternal = useCallback((skipCallback = false): Promise<void> => {
    return new Promise((resolve) => {
      if (lifecycleStateRef.current === 'CLOSED') {
        console.log('🛑 [PlayerContext] closePlayer ignored - already CLOSED');
        resolve();
        return;
      }

      if (lifecycleStateRef.current === 'CLOSING') {
        console.log('🛑 [PlayerContext] closePlayer ignored - already CLOSING');
        resolve();
        return;
      }

      lifecycleStateRef.current = 'CLOSING';
      console.log('🎬 [PlayerContext] Closing player (state: CLOSING)');

      // Released before the origin screen regains focus, so the page_view sent
      // after the player closes is kept.
      releasePageViewSuppression();

      setPlayerState(prev => {
        if (prev.isForYouMode) {
          console.log('🎬 [PlayerContext] Setting closedFromForYou flag');
          closedFromForYouRef.current = true;
        }

        const callback = prev.onCloseCallback;

        if (callback && !skipCallback) {
          console.log('🎬 [PlayerContext] Executing onCloseCallback synchronously');
          callback();
        }

        return INITIAL_STATE;
      });

      setTimeout(() => {
        lifecycleStateRef.current = 'CLOSED';
        console.log('🎬 [PlayerContext] Player closed (state: CLOSED)');
        resolve();
      }, 100);
    });
  }, []);

  const closePlayer = useCallback(() => {
    closePlayerInternal(false);
  }, [closePlayerInternal]);

  const openPlayerWithSeries = useCallback(async (
    seriesId: string,
    campaignCountriesLanguagesId: string | null,
    smartuserId?: string,
    resumeEpisodeId?: string,
    startTimeSeconds?: number
  ) => {
    if (!campaignCountriesLanguagesId) {
      console.error('🎬 [PlayerContext] Cannot open player: no campaignCountriesLanguagesId');
      return;
    }

    if (openInProgressRef.current) {
      console.log('🛑 [PlayerContext] openPlayerWithSeries blocked - already in progress (synchronous check)');
      return;
    }
    openInProgressRef.current = true;

    if (loginCoordinator.shouldDeferAction()) {
      console.log('🎬 [PlayerContext] Login transaction in progress, enqueueing openPlayerWithSeries');
      openInProgressRef.current = false;
      loginCoordinator.enqueueAction(`player:series:${seriesId}`, async () => {
        console.log('🎬 [PlayerContext] Executing enqueued openPlayerWithSeries after login completion');
        openPlayerWithSeries(seriesId, campaignCountriesLanguagesId, smartuserId, resumeEpisodeId, startTimeSeconds);
      });
      return;
    }

    const canProceed = await checkDebounceAndStability('openPlayerWithSeries');
    if (!canProceed) {
      openInProgressRef.current = false;
      return;
    }

    // Opening the player as an overlay can blur then refocus the origin screen
    // (Home, My List), whose focus effect would send a page_view right before
    // the dv_content_consumption event.
    suppressPageViewsForPlayerLaunch();

    try {
      const currentState = lifecycleStateRef.current;

      if (currentState === 'OPEN' || currentState === 'OPENING') {
        console.log('🎬 [PlayerContext] Player already open/opening, closing first before re-opening');
        await closePlayerInternal(true);
        await new Promise(r => setTimeout(r, 150));
      }

      if (lifecycleStateRef.current === 'CLOSING') {
        console.log('🎬 [PlayerContext] Waiting for close to complete...');
        await new Promise(r => setTimeout(r, 200));
      }

      lifecycleStateRef.current = 'OPENING';
      console.log('🎬 [PlayerContext] Opening player with series (state: OPENING)');

      setPlayerState(prev => ({
        ...prev,
        isVisible: true,
        seriesId: seriesId,
        isLoading: true,
      }));

      console.log('🎬 [PlayerContext] Loading episodes for series:', seriesId);

      let finalResumeEpisodeId = resumeEpisodeId;
      let finalStartTimeSeconds = startTimeSeconds;

      if (smartuserId && !resumeEpisodeId) {
        console.log('🎬 [PlayerContext] User authenticated, checking for resume position');
        const resumeInfo = await ContentService.getResumeEpisodeForSeries(
          campaignCountriesLanguagesId,
          seriesId,
          smartuserId
        );

        if (resumeInfo) {
          finalResumeEpisodeId = resumeInfo.episodeId;
          finalStartTimeSeconds = resumeInfo.startTimeSeconds;
          console.log('🎬 [PlayerContext] Resume position found:', {
            episodeId: finalResumeEpisodeId,
            startTime: finalStartTimeSeconds
          });
        } else {
          console.log('🎬 [PlayerContext] No resume position, starting from episode 1');
        }
      }

      const allSeriesEpisodes = await ContentService.getAllEpisodesForSeries(
        campaignCountriesLanguagesId,
        seriesId
      );

      if (allSeriesEpisodes.length > 0) {
        const initialEpisodeId = finalResumeEpisodeId || allSeriesEpisodes[0].id;

        console.log('🎬 [PlayerContext] Opening player with series:', {
          seriesId,
          initialEpisodeId,
          episodesCount: allSeriesEpisodes.length,
          startTimeSeconds: finalStartTimeSeconds,
          isResume: !!finalResumeEpisodeId
        });

        setPlayerState({
          isVisible: true,
          seriesId: seriesId,
          episodes: allSeriesEpisodes,
          initialEpisodeId: initialEpisodeId,
          startTimeSeconds: finalStartTimeSeconds,
          isLoading: false,
          isForYouMode: false,
          onCloseCallback: undefined,
        });

        lifecycleStateRef.current = 'OPEN';
        console.log('🎬 [PlayerContext] Player opened (state: OPEN)');
      } else {
        console.error('🎬 [PlayerContext] No episodes found for series:', seriesId);
        lifecycleStateRef.current = 'CLOSED';
        setPlayerState(INITIAL_STATE);
        Alert.alert('Error', 'No episodes found for this series');
      }
    } catch (error) {
      console.error('🎬 [PlayerContext] Error loading series episodes:', error);
      lifecycleStateRef.current = 'CLOSED';
      setPlayerState(INITIAL_STATE);
      Alert.alert('Error', 'Failed to load episodes');
    } finally {
      openInProgressRef.current = false;
    }
  }, [closePlayerInternal, checkDebounceAndStability]);

  const openPlayerWithEpisode = useCallback(async (
    episodeId: string,
    episodes: Episode[],
    startTimeSeconds?: number
  ) => {
    if (openInProgressRef.current) {
      console.log('🛑 [PlayerContext] openPlayerWithEpisode blocked - already in progress (synchronous check)');
      return;
    }
    openInProgressRef.current = true;

    if (loginCoordinator.shouldDeferAction()) {
      console.log('🎬 [PlayerContext] Login transaction in progress, enqueueing openPlayerWithEpisode');
      openInProgressRef.current = false;
      loginCoordinator.enqueueAction(`player:episode:${episodeId}`, async () => {
        console.log('🎬 [PlayerContext] Executing enqueued openPlayerWithEpisode after login completion');
        openPlayerWithEpisode(episodeId, episodes, startTimeSeconds);
      });
      return;
    }

    const canProceed = await checkDebounceAndStability('openPlayerWithEpisode');
    if (!canProceed) {
      openInProgressRef.current = false;
      return;
    }

    try {
      const currentState = lifecycleStateRef.current;

      if (currentState === 'OPEN' || currentState === 'OPENING') {
        console.log('🎬 [PlayerContext] Player already open/opening, closing first');
        await closePlayerInternal(true);
        await new Promise(r => setTimeout(r, 150));
      }

      if (lifecycleStateRef.current === 'CLOSING') {
        await new Promise(r => setTimeout(r, 200));
      }

      lifecycleStateRef.current = 'OPENING';
      console.log('🎬 [PlayerContext] Opening player with specific episode (state: OPENING):', {
        episodeId,
        episodesCount: episodes.length,
        startTimeSeconds
      });

      setPlayerState({
        isVisible: true,
        episodes: episodes,
        seriesId: undefined,
        initialEpisodeId: episodeId,
        startTimeSeconds: startTimeSeconds,
        isLoading: false,
        isForYouMode: false,
        onCloseCallback: undefined,
      });

      lifecycleStateRef.current = 'OPEN';
      console.log('🎬 [PlayerContext] Player opened (state: OPEN)');
    } finally {
      openInProgressRef.current = false;
    }
  }, [closePlayerInternal, checkDebounceAndStability]);

  const openPlayerWithEpisodes = useCallback(async (
    episodes: Episode[],
    initialEpisodeId?: string,
    startTimeSeconds?: number,
    options?: { isForYouMode?: boolean; onCloseCallback?: () => void; seriesId?: string }
  ) => {
    if (openInProgressRef.current) {
      console.log('🛑 [PlayerContext] openPlayerWithEpisodes blocked - already in progress (synchronous check)');
      return;
    }
    openInProgressRef.current = true;

    if (loginCoordinator.shouldDeferAction()) {
      const actionKey = initialEpisodeId ? `player:episodes:${initialEpisodeId}` : 'player:episodes:batch';
      console.log('🎬 [PlayerContext] Login transaction in progress, enqueueing openPlayerWithEpisodes');
      openInProgressRef.current = false;
      loginCoordinator.enqueueAction(actionKey, async () => {
        console.log('🎬 [PlayerContext] Executing enqueued openPlayerWithEpisodes after login completion');
        openPlayerWithEpisodes(episodes, initialEpisodeId, startTimeSeconds, options);
      });
      return;
    }

    const canProceed = await checkDebounceAndStability('openPlayerWithEpisodes');
    if (!canProceed) {
      openInProgressRef.current = false;
      return;
    }

    try {
      const currentState = lifecycleStateRef.current;

      if (currentState === 'OPEN' || currentState === 'OPENING') {
        console.log('🎬 [PlayerContext] Player already open/opening, closing first');
        await closePlayerInternal(true);
        await new Promise(r => setTimeout(r, 150));
      }

      if (lifecycleStateRef.current === 'CLOSING') {
        await new Promise(r => setTimeout(r, 200));
      }

      lifecycleStateRef.current = 'OPENING';

      if (Platform.OS === 'web' && options?.isForYouMode && !initialEpisodeId) {
        console.error('🛑 [PlayerContext][WEB] CRITICAL: For You player opened without initialEpisodeId!');
        if (__DEV__) {
          throw new Error('[ForYou][Web] Player opened without initialEpisodeId - this will cause playback issues');
        }
      }

      console.log('🎬 [PlayerContext] Opening player with episodes list (state: OPENING):', {
        episodesCount: episodes.length,
        initialEpisodeId,
        startTimeSeconds,
        isForYouMode: options?.isForYouMode ?? false,
        seriesId: options?.seriesId
      });

      setPlayerState({
        isVisible: true,
        episodes: episodes,
        seriesId: options?.seriesId,
        initialEpisodeId: initialEpisodeId,
        startTimeSeconds: startTimeSeconds,
        isLoading: false,
        isForYouMode: options?.isForYouMode ?? false,
        onCloseCallback: options?.onCloseCallback,
      });

      lifecycleStateRef.current = 'OPEN';
      console.log('🎬 [PlayerContext] Player opened (state: OPEN)');
    } finally {
      openInProgressRef.current = false;
    }
  }, [closePlayerInternal, checkDebounceAndStability]);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const openPlayerForYou = useCallback(async (
    campaignCountriesLanguagesId: string | null,
    options?: { onCloseCallback?: () => void }
  ) => {
    if (!campaignCountriesLanguagesId) {
      console.error('🎬 [PlayerContext] Cannot open For You player: no campaignCountriesLanguagesId');
      return;
    }

    if (openInProgressRef.current) {
      console.log('🛑 [PlayerContext] openPlayerForYou blocked - already in progress (synchronous check)');
      return;
    }
    openInProgressRef.current = true;

    if (loginCoordinator.shouldDeferAction()) {
      console.log('🎬 [PlayerContext] Login transaction in progress, enqueueing openPlayerForYou');
      openInProgressRef.current = false;
      loginCoordinator.enqueueAction('player:foryou', async () => {
        console.log('🎬 [PlayerContext] Executing enqueued openPlayerForYou after login completion');
        openPlayerForYou(campaignCountriesLanguagesId, options);
      });
      return;
    }

    const canProceed = await checkDebounceAndStability('openPlayerForYou');
    if (!canProceed) {
      openInProgressRef.current = false;
      return;
    }

    suppressPageViewsForPlayerLaunch();

    try {
      const currentState = lifecycleStateRef.current;

      if (currentState === 'OPEN' || currentState === 'OPENING') {
        console.log('🎬 [PlayerContext] Player already open/opening, closing first before re-opening For You');
        await closePlayerInternal(true);
        await new Promise(r => setTimeout(r, 150));
      }

      if (lifecycleStateRef.current === 'CLOSING') {
        console.log('🎬 [PlayerContext] Waiting for close to complete...');
        await new Promise(r => setTimeout(r, 200));
      }

      lifecycleStateRef.current = 'OPENING';
      console.log('🎬 [PlayerContext] Opening For You player (state: OPENING)');

      setPlayerState(prev => ({
        ...prev,
        isVisible: true,
        isLoading: true,
        isForYouMode: true,
      }));

      console.log('🎬 [PlayerContext] Fetching first episodes of all series for For You...');
      const freeEpisodesCount = campaignCache.getFreeEpisodesCount(campaignCountriesLanguagesId);
      const firstEpisodes = await ContentService.getFirstEpisodesOfAllSeries(campaignCountriesLanguagesId, freeEpisodesCount);

      if (firstEpisodes.length === 0) {
        console.error('🎬 [PlayerContext] No episodes found for For You player');
        lifecycleStateRef.current = 'CLOSED';
        setPlayerState(INITIAL_STATE);
        Alert.alert('Error', 'No content available');
        return;
      }

      const shuffledEpisodes = shuffleArray(firstEpisodes);
      const initialEpisode = shuffledEpisodes.find(episode => episode.video_url?.trim()) || shuffledEpisodes[0];

      console.log('🎬 [PlayerContext] Opening For You player:', {
        episodesCount: shuffledEpisodes.length,
        initialEpisodeId: initialEpisode.id,
        initialSeriesId: initialEpisode.seriesId
      });

      setPlayerState({
        isVisible: true,
        episodes: shuffledEpisodes,
        seriesId: initialEpisode.seriesId,
        initialEpisodeId: initialEpisode.id,
        startTimeSeconds: undefined,
        isLoading: false,
        isForYouMode: true,
        onCloseCallback: options?.onCloseCallback,
      });

      lifecycleStateRef.current = 'OPEN';
      console.log('🎬 [PlayerContext] For You player opened (state: OPEN)');
    } catch (error) {
      console.error('🎬 [PlayerContext] Error loading For You episodes:', error);
      lifecycleStateRef.current = 'CLOSED';
      setPlayerState(INITIAL_STATE);
      Alert.alert('Error', 'Failed to load content');
    } finally {
      openInProgressRef.current = false;
    }
  }, [closePlayerInternal, checkDebounceAndStability]);

  const wasClosedFromForYou = useCallback(() => {
    return closedFromForYouRef.current;
  }, []);

  const clearClosedFromForYouFlag = useCallback(() => {
    console.log('🎬 [PlayerContext] Clearing closedFromForYou flag');
    closedFromForYouRef.current = false;
  }, []);

  const value: PlayerContextType = {
    ...playerState,
    openPlayerWithSeries,
    openPlayerWithEpisode,
    openPlayerWithEpisodes,
    openPlayerForYou,
    closePlayer,
    setPlayerLoading,
    wasClosedFromForYou,
    clearClosedFromForYouFlag,
    getLifecycleState,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer(): PlayerContextType {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}
